import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { commercialStatsAPI, adminJournalAPI } from '../lib/api';
import { Link } from 'react-router-dom';
import { RenewalAlertCard } from '../components/RenewalAlertCard';
import { toast } from 'sonner';
import { RefreshCw, Users, MousePointerClick, BadgeCheck, Euro, ExternalLink, ShieldCheck, AlertTriangle, Mail, Receipt } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';

const POSTHOG_URL = 'https://us.posthog.com';
const PERIODS = [7, 30, 90];
const eur = (v) => (v === null || v === undefined ? '—' : `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
const pct = (v) => (v === null || v === undefined ? '—' : `${v.toLocaleString('fr-FR')} %`);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const STATUS_STYLE = {
  'Mesurés': 'bg-sky-100 text-sky-800',
  'Confirmés': 'bg-emerald-100 text-emerald-800',
  'Vérifié': 'bg-emerald-100 text-emerald-800',
  'Non instrumenté': 'bg-slate-200 text-slate-700',
};

const KpiTile = ({ icon: Icon, label, value, status, detail, testId }) => (
  <Card data-testid={testId} className="border-slate-200">
    <CardContent className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-600">
          <Icon className="h-4 w-4 text-emerald-600" />{label}
        </div>
        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[status] || 'bg-slate-100 text-slate-700'}`}>{status}</span>
      </div>
      <div className="mt-3 text-3xl font-bold font-['Manrope'] text-slate-900" data-testid={`${testId}-value`}>{value}</div>
      <p className="mt-1 text-xs text-slate-500">{detail}</p>
    </CardContent>
  </Card>
);

const SourceBadge = ({ source }) => (
  <span className={`text-[11px] px-2 py-0.5 rounded-full ${source.startsWith('preuve') ? 'bg-emerald-100 text-emerald-800' : 'bg-sky-100 text-sky-800'}`}>
    {source.startsWith('preuve') ? 'Preuve serveur' : 'Mesure first-party'}
  </span>
);

const Funnel = ({ tunnel }) => {
  const max = Math.max(1, ...tunnel.etapes.map((e) => e.valeur));
  return (
    <div className="space-y-3" data-testid="funnel-steps">
      {tunnel.etapes.map((e, i) => (
        <div key={e.etape} data-testid={`funnel-step-${i}`}>
          <div className="flex items-center justify-between text-sm mb-1 gap-2 flex-wrap">
            <span className="font-medium text-slate-800">{i + 1}. {e.etape}</span>
            <span className="flex items-center gap-2"><SourceBadge source={e.source} /><strong className="tabular-nums">{e.valeur}</strong></span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full rounded-full ${e.source.startsWith('preuve') ? 'bg-emerald-500' : 'bg-sky-500'}`} style={{ width: `${Math.max(2, (e.valeur / max) * 100)}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
};

const DistributionTable = ({ data, label, testId }) => {
  const rows = Object.entries(data || {});
  const total = rows.reduce((s, [, v]) => s + v, 0);
  if (!rows.length) return <p className="text-sm text-slate-500" data-testid={`${testId}-empty`}>Aucune donnée sur la période.</p>;
  return (
    <Table data-testid={testId}>
      <TableHeader><TableRow><TableHead>{label}</TableHead><TableHead className="text-right">Visites</TableHead><TableHead className="text-right">Part</TableHead></TableRow></TableHeader>
      <TableBody>
        {rows.map(([k, v]) => (
          <TableRow key={k}><TableCell className="font-medium">{k}</TableCell><TableCell className="text-right tabular-nums">{v}</TableCell><TableCell className="text-right tabular-nums">{total ? Math.round((v / total) * 100) : 0} %</TableCell></TableRow>
        ))}
      </TableBody>
    </Table>
  );
};


const EmailHealthCard = () => {
  const [s, setS] = useState(null);
  useEffect(() => { adminJournalAPI.emails(1).then((r) => setS(r.data.summary)).catch(() => setS(false)); }, []);
  if (s === null) return null;
  const failures = s ? s.echecs : 0;
  const alert = failures > 0;
  return (
    <Card className={alert ? 'border-red-400 bg-red-50/40' : 'border-emerald-200'} data-testid="email-health-card">
      <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <span className={`inline-flex h-3 w-3 rounded-full ${alert ? 'bg-red-500 animate-pulse' : 'bg-emerald-500'}`} data-testid="email-health-indicator" aria-label={alert ? 'Alerte emails' : 'Emails OK'} />
          <Mail className={`h-5 w-5 ${alert ? 'text-red-600' : 'text-emerald-700'}`} />
          <div>
            <div className="font-semibold text-sm" data-testid="email-health-title">{s === false ? 'Suivi des emails indisponible' : alert ? `${failures} email(s) de bienvenue en échec ou refusé(s)` : 'Emails de bienvenue : aucun échec'}</div>
            {s && (
              <div className="text-xs text-slate-600 flex flex-wrap gap-x-3" data-testid="email-health-detail">
                <span>Acceptés par Brevo (non confirmés) : <strong>{s.acceptes_non_confirmes}</strong></span>
                <span>Délivrés (confirmés) : <strong>{s.delivres_confirmes}</strong></span>
                <span>Rejetés : <strong>{s.rejetes}</strong></span>
                <span>Échecs d'envoi : <strong>{s.echecs_envoi}</strong></span>
                <span>Inconnus / en attente : <strong>{s.inconnus_en_attente}</strong></span>
              </div>
            )}
          </div>
        </div>
        <Link to="/admin/paiements" data-testid="email-health-link"><Button variant="outline" size="sm"><Receipt className="h-4 w-4 mr-1" />Journal paiements &amp; emails</Button></Link>
      </CardContent>
    </Card>
  );
};

const AdminCommercialPage = () => {
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data } = await commercialStatsAPI.get(d);
      setStats(data);
    } catch (e) {
      toast.error('Impossible de charger les statistiques commerciales');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !stats) {
    return <div className="flex items-center justify-center py-24 text-slate-500" data-testid="commercial-loading"><RefreshCw className="h-5 w-5 animate-spin mr-2" />Chargement…</div>;
  }
  if (!stats) return null;
  const { kpi, acquisition, tunnel, resultats, campagnes, fiabilite } = stats;

  return (
    <div className="space-y-8" data-testid="admin-commercial-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope']">Tableau de bord commercial</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Dernière actualisation : <span data-testid="generated-at">{fmtDate(stats.genere_le)}</span>
            {stats.collecte_depuis && <> · Collecte depuis le {fmtDate(stats.collecte_depuis)}</>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex rounded-full border bg-white p-1" role="tablist" data-testid="period-selector">
            {PERIODS.map((p) => (
              <button key={p} type="button" role="tab" aria-selected={days === p} onClick={() => setDays(p)} data-testid={`period-${p}`}
                className={`px-4 py-1.5 rounded-full text-sm font-semibold ${days === p ? 'bg-emerald-600 text-white' : 'text-slate-600 hover:text-slate-900'}`}>
                {p} j
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => load()} data-testid="refresh-btn"><RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Actualiser</Button>
          <a href={POSTHOG_URL} target="_blank" rel="noopener noreferrer" data-testid="posthog-link">
            <Button variant="ghost" size="sm"><ExternalLink className="h-4 w-4 mr-1" />PostHog</Button>
          </a>
        </div>
      </div>

      <EmailHealthCard />
      <RenewalAlertCard />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="kpi-tiles">
        <KpiTile icon={Users} label="Visiteurs" value={kpi.visiteurs.valeur} status={kpi.visiteurs.statut} detail={kpi.visiteurs.detail} testId="kpi-visiteurs" />
        <KpiTile icon={MousePointerClick} label="Clics vers l'achat" value={kpi.clics_achat.valeur} status={kpi.clics_achat.statut} detail={kpi.clics_achat.detail} testId="kpi-clics" />
        <KpiTile icon={BadgeCheck} label="Abonnements" value={kpi.abonnements.valeur} status={kpi.abonnements.statut} detail={kpi.abonnements.detail} testId="kpi-abonnements" />
        <KpiTile icon={Euro} label="CA encaissé" value={eur(kpi.ca_encaisse.valeur)} status={kpi.ca_encaisse.statut} detail={kpi.ca_encaisse.detail} testId="kpi-ca" />
      </div>

      <section className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2" data-testid="acquisition-card">
          <CardHeader><CardTitle className="text-lg">1. Acquisition — évolution sur {days} jours</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-3 mb-4 text-sm">
              <div><p className="text-slate-500">Visites</p><p className="text-xl font-bold" data-testid="acq-visites">{acquisition.visites}</p></div>
              <div><p className="text-slate-500">Visiteurs uniques / jour</p><p className="text-xl font-bold" data-testid="acq-visiteurs">{acquisition.visiteurs_uniques_jour}</p></div>
              <div><p className="text-slate-500">Visites page /marque</p><p className="text-xl font-bold" data-testid="acq-marque">{acquisition.visites_page_commerciale}</p></div>
            </div>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={acquisition.evolution} margin={{ left: -20, right: 8, top: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="jour" tick={{ fontSize: 11 }} tickFormatter={(d) => d.slice(5)} minTickGap={24} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip />
                  <Area type="monotone" dataKey="visites" name="Visites" stroke="#0ea5e9" fill="#bae6fd" />
                  <Area type="monotone" dataKey="clics" name="Clics offres" stroke="#059669" fill="#a7f3d0" />
                  <Area type="monotone" dataKey="paiements" name="Paiements confirmés" stroke="#7c3aed" fill="#ddd6fe" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="sources-card">
          <CardHeader><CardTitle className="text-lg">Sources de trafic</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <DistributionTable data={acquisition.par_canal} label="Canal" testId="sources-table" />
            <DistributionTable data={acquisition.par_appareil} label="Appareil" testId="devices-table" />
          </CardContent>
        </Card>
      </section>

      <section className="grid lg:grid-cols-2 gap-6">
        <Card data-testid="funnel-card">
          <CardHeader><CardTitle className="text-lg">2. Tunnel de conversion</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <Funnel tunnel={tunnel} />
            <div className="grid grid-cols-2 gap-3 text-sm pt-2 border-t">
              <div><p className="text-slate-500">Clics offre mensuelle</p><p className="font-bold" data-testid="clics-mensuel">{tunnel.clics_mensuel}</p></div>
              <div><p className="text-slate-500">Clics offre annuelle</p><p className="font-bold" data-testid="clics-annuel">{tunnel.clics_annuel}</p></div>
              <div><p className="text-slate-500">Visite → clic</p><p className="font-bold">{pct(tunnel.taux.visite_vers_clic_pct)}</p></div>
              <div><p className="text-slate-500">Clic → commande</p><p className="font-bold">{pct(tunnel.taux.clic_vers_commande_pct)}</p></div>
              <div><p className="text-slate-500">Commande → paiement</p><p className="font-bold">{pct(tunnel.taux.commande_vers_paiement_pct)}</p></div>
              <div><p className="text-slate-500">Visite → abonnement</p><p className="font-bold">{pct(tunnel.taux.visite_vers_abonnement_pct)}</p></div>
            </div>
            {tunnel.clics_sortants_systemeio > 0 && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2">
                {tunnel.clics_sortants_systemeio} clic(s) sortant(s) vers Systeme.io : un clic sortant n&apos;est pas une vente ; seules les ventes confirmées par webhook sont comptées.
              </p>
            )}
          </CardContent>
        </Card>
        <Card data-testid="results-card">
          <CardHeader><CardTitle className="text-lg">3. Résultats commerciaux (données de paiement vérifiées)</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><p className="text-slate-500">Nouveaux abonnements mensuels</p><p className="text-xl font-bold" data-testid="res-new-mensuel">{resultats.nouveaux_abonnements.mensuel}</p></div>
              <div><p className="text-slate-500">Nouveaux abonnements annuels</p><p className="text-xl font-bold" data-testid="res-new-annuel">{resultats.nouveaux_abonnements.annuel}</p></div>
              <div><p className="text-slate-500">Abonnés actifs (mensuel / annuel)</p><p className="text-xl font-bold" data-testid="res-actifs">{resultats.abonnes_actifs.mensuel} / {resultats.abonnes_actifs.annuel}</p><p className="text-xs text-slate-400">+ {resultats.abonnes_actifs.acces_a_vie_legacy} accès à vie (historique)</p></div>
              <div><p className="text-slate-500">CA encaissé (PayPal IPN)</p><p className="text-xl font-bold" data-testid="res-ca">{eur(resultats.ca_encaisse_eur)}</p><p className="text-xs text-slate-400">{resultats.paiements_confirmes} paiement(s) confirmé(s)</p></div>
              <div><p className="text-slate-500">MRR</p><p className="text-xl font-bold" data-testid="res-mrr">{eur(resultats.mrr_eur)}</p><p className="text-xs text-slate-400">ARR {eur(resultats.arr_eur)}</p></div>
              <div><p className="text-slate-500">Résiliations</p><p className="text-xl font-bold text-slate-400">—</p><Badge variant="secondary" className="text-[10px]">{resultats.resiliations.statut}</Badge></div>
            </div>
            {resultats.ventes_systemeio_webhook > 0 && <p className="mt-3 text-xs text-slate-500">{resultats.ventes_systemeio_webhook} vente(s) confirmée(s) par webhook Systeme.io sur la période (hors CA PayPal).</p>}
          </CardContent>
        </Card>
      </section>

      <Card data-testid="campaigns-card">
        <CardHeader><CardTitle className="text-lg">4. Analyse des campagnes (canal × UTM)</CardTitle></CardHeader>
        <CardContent>
          {campagnes.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="campaigns-empty">Aucune visite attribuée sur la période. Les campagnes doivent utiliser des liens avec paramètres utm_source / utm_medium / utm_campaign.</p>
          ) : (
            <Table data-testid="campaigns-table">
              <TableHeader><TableRow><TableHead>Canal</TableHead><TableHead>Campagne</TableHead><TableHead className="text-right">Visites</TableHead><TableHead className="text-right">Clics</TableHead><TableHead className="text-right">Commandes</TableHead><TableHead className="text-right">Paiements</TableHead><TableHead className="text-right">Abonnements</TableHead></TableRow></TableHeader>
              <TableBody>
                {campagnes.map((c, i) => (
                  <TableRow key={i}><TableCell className="font-medium">{c.canal}</TableCell><TableCell className="text-slate-600">{c.campagne}</TableCell><TableCell className="text-right tabular-nums">{c.visites}</TableCell><TableCell className="text-right tabular-nums">{c.clics}</TableCell><TableCell className="text-right tabular-nums">{c.commandes}</TableCell><TableCell className="text-right tabular-nums">{c.paiements}</TableCell><TableCell className="text-right tabular-nums font-semibold">{c.activations}</TableCell></TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card data-testid="reliability-card" className="border-slate-200 bg-slate-50/60">
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-emerald-600" />5. Fiabilité des données</CardTitle></CardHeader>
        <CardContent className="text-sm text-slate-700 space-y-2">
          <p><Badge className="bg-sky-100 text-sky-800 mr-2">Mesure first-party</Badge>{fiabilite.mesure_first_party}</p>
          <p><Badge className="bg-emerald-100 text-emerald-800 mr-2">Preuve serveur</Badge>{fiabilite.preuve_serveur}</p>
          <p><Badge variant="secondary" className="mr-2">PostHog</Badge>{fiabilite.posthog.note} Événements relayés après consentement : <strong data-testid="ph-relayed">{fiabilite.posthog.evenements_relayes_apres_consentement}</strong>.</p>
          <p className="flex items-start gap-2"><AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            Consentement : {fiabilite.consentement.vues_avec_consentement} vue(s) sur {fiabilite.consentement.vues_total} avec consentement ({pct(fiabilite.consentement.taux_pct)}) — {fiabilite.consentement.acceptations} acceptation(s), {fiabilite.consentement.refus} refus. Les parcours multi-pages PostHog ne couvrent que les visiteurs consentants.
          </p>
          <p className="text-xs text-slate-500">{fiabilite.exclusions} {fiabilite.historique}</p>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminCommercialPage;
